import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  CalendarDays, ChevronLeft, ChevronRight, Clock, CheckCircle,
  Loader2, User, Calendar, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addMonths = (date, n) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

/**
 * SlotPicker — shows available time slots for a given date.
 * Privacy: only shows start/end and status (available/taken).
 */
const SlotPicker = ({ teacherId, date, onSelect, selectedSlot, bookingState }) => {
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teacherId || !date) return;
    setLoading(true);
    setSlots(null);
    axios.get(`${API_URL}/api/student/schedule/available-slots?teacher_id=${teacherId}&date=${date}`)
      .then(res => setSlots(res.data.slots || []))
      .catch(() => toast.error('Failed to load slots'))
      .finally(() => setLoading(false));
  }, [teacherId, date]);

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 text-[#D72B2B] animate-spin" />
    </div>
  );

  const available = (slots || []).filter(s => s.status === 'available');

  if (!available.length) return (
    <div className="text-center py-8">
      <Clock className="w-10 h-10 text-[#1A2E6E]/20 mx-auto mb-3" />
      <p className="text-sm text-[#1A2E6E]/50">No available slots on this day.</p>
    </div>
  );

  return (
    <div className="space-y-2">
      {(slots || []).map((slot, i) => {
        const isAvailable = slot.status === 'available';
        const isSelected = selectedSlot?.start === slot.start && selectedSlot?.end === slot.end;
        const isOwnBooking = slot.own_booking != null;
        return (
              <button
                key={i}
                disabled={!isAvailable || bookingState === 'loading'}
                onClick={() => isAvailable && onSelect(slot)}
                data-testid={`schedule-slot-${slot.start.replace(':', '-')}-${slot.end.replace(':', '-')}`}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                  isSelected
                    ? 'border-[#D72B2B] bg-[#D72B2B]/5 text-[#D72B2B]'
                : isOwnBooking
                  ? 'border-[#4CAF50]/50 bg-[#4CAF50]/5 text-[#4CAF50] cursor-default'
                  : isAvailable
                    ? 'border-[#E8EAF0] bg-white hover:border-[#1A2E6E]/40 text-[#1A2E6E] cursor-pointer'
                    : 'border-[#E8EAF0] bg-[#F5F5F5] text-[#1A2E6E]/30 cursor-not-allowed'
            }`}
          >
            <span className="flex items-center gap-2">
              <Clock size={14} />
              {slot.start} – {slot.end}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isSelected
                ? 'bg-[#D72B2B]/10 text-[#D72B2B]'
                : isOwnBooking
                  ? 'bg-[#4CAF50]/10 text-[#4CAF50]'
                  : isAvailable
                    ? 'bg-[#4CAF50]/10 text-[#4CAF50]'
                    : 'bg-[#E8EAF0] text-[#1A2E6E]/40'
            }`}>
              {isOwnBooking ? 'Your booking' : isAvailable ? 'Available' : 'Taken'}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * MiniCalendar — month view with available day highlighting.
 */
const MiniCalendar = ({ teacherId, selectedDate, onSelectDate }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [monthDate, setMonthDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [availableDays, setAvailableDays] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchAvailableDays = useCallback(async () => {
    if (!teacherId) return;
    const start = toDateStr(monthDate);
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const end = toDateStr(lastDay);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/student/schedule/available-days?teacher_id=${teacherId}&start=${start}&end=${end}`
      );
      const map = {};
      for (const d of res.data) map[d.date] = d;
      setAvailableDays(map);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [teacherId, monthDate]);

  useEffect(() => { fetchAvailableDays(); }, [fetchAvailableDays]);

  const firstDayOfMonth = startOfMonth(monthDate).getDay(); // 0=Sun
  const totalDays = daysInMonth(monthDate);

  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const monthLabel = `${MONTHS[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

  return (
    <div className="bg-white rounded-xl border border-[#E8EAF0] p-4">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setMonthDate(d => addMonths(d, -1))}
          disabled={monthDate <= new Date(today.getFullYear(), today.getMonth(), 1)}
          className="p-1.5 rounded-lg hover:bg-[#F5F7FA] disabled:opacity-30 transition-colors"
        >
          <ChevronLeft size={16} className="text-[#1A2E6E]" />
        </button>
        <span className="text-sm font-semibold text-[#1A2E6E]">{monthLabel}</span>
        <button
          onClick={() => setMonthDate(d => addMonths(d, 1))}
          disabled={monthDate >= addMonths(today, 2)}
          className="p-1.5 rounded-lg hover:bg-[#F5F7FA] disabled:opacity-30 transition-colors"
        >
          <ChevronRight size={16} className="text-[#1A2E6E]" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-2">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-center text-[10px] font-semibold text-[#1A2E6E]/40 uppercase py-1">{d}</div>
        ))}
      </div>

      {/* Cells */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 text-[#D72B2B] animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const dateStr = toDateStr(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
            const todayStr = toDateStr(today);
            const isPast = dateStr < todayStr;
            const isToday = dateStr === todayStr;
            const isSelected = selectedDate === dateStr;
            const info = availableDays[dateStr];
            const hasSlots = !!info?.available_slots;

            return (
              <button
                key={i}
                disabled={isPast || !hasSlots}
                onClick={() => !isPast && hasSlots && onSelectDate(dateStr)}
                data-testid={`schedule-date-${dateStr}`}
                className={`relative h-9 w-full rounded-lg text-sm font-medium transition-all ${
                  isSelected
                    ? 'bg-[#D72B2B] text-white'
                    : isToday
                      ? 'ring-2 ring-[#1A2E6E]/30 text-[#1A2E6E]'
                      : hasSlots
                        ? 'hover:bg-[#1A2E6E]/5 text-[#1A2E6E] cursor-pointer'
                        : isPast
                          ? 'text-[#1A2E6E]/20 cursor-default'
                          : 'text-[#1A2E6E]/30 cursor-default'
                }`}
              >
                {day}
                {hasSlots && !isSelected && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#4CAF50]" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-[#1A2E6E]/40 mt-3 flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-[#4CAF50] inline-block" />
        Dates with available slots
      </p>
    </div>
  );
};

/**
 * StudentSchedule — full scheduling page.
 * Can also be used as an embedded component (via mode="embed") within onboarding.
 */
export const StudentSchedule = ({ mode = 'page', onBooked, onSkip }) => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingState, setBookingState] = useState('idle'); // idle | loading | success | error
  const [bookedClass, setBookedClass] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/student/schedule/teachers`)
      .then(res => {
        setTeachers(res.data);
        if (res.data.length > 0) setSelectedTeacher(res.data[0]);
      })
      .catch(() => toast.error('Failed to load teachers'))
      .finally(() => setLoadingTeachers(false));
  }, []);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBook = async () => {
    if (!selectedTeacher || !selectedDate || !selectedSlot) return;
    setBookingState('loading');
    try {
      const res = await axios.post(`${API_URL}/api/student/schedule/book`, {
        teacher_id: selectedTeacher.id,
        date: selectedDate,
        start: selectedSlot.start,
        end: selectedSlot.end,
      });
      setBookedClass(res.data);
      setBookingState('success');
      toast.success('Class booked!');
      if (onBooked) onBooked(res.data);
    } catch (err) {
      setBookingState('idle');
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to book class. Please try again.');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loadingTeachers) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
    </div>
  );

  if (!teachers.length) return (
    <div className={`${mode === 'page' ? 'max-w-xl mx-auto py-12 px-4 text-center' : 'text-center py-8'}`}>
      <User className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-3" />
      <p className="text-[#1A2E6E]/60 text-sm">No teachers available to schedule with yet.<br />Your teacher will reach out soon!</p>
      {mode === 'page' && (
        <Link
          to="/student/available"
          className="inline-flex items-center gap-2 mt-4 px-4 py-2 border border-[#E8EAF0] rounded-xl text-sm font-medium text-[#1A2E6E] hover:bg-[#F5F7FA] transition-colors"
        >
          <CalendarDays size={14} />
          View open classes
        </Link>
      )}
      {mode === 'embed' && onSkip && (
        <button onClick={onSkip} className="mt-4 text-sm text-[#1A2E6E]/50 hover:text-[#1A2E6E] underline">
          Continue without scheduling
        </button>
      )}
    </div>
  );

  // Success state
  if (bookingState === 'success' && bookedClass) {
    const content = (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`text-center space-y-5 ${mode === 'embed' ? 'py-4' : 'max-w-md mx-auto py-12 px-4'}`}
      >
        <div className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-[#4CAF50]" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Class Booked!</h3>
          <p className="text-[#1A2E6E]/60 mt-2 text-sm">Your first class is confirmed.</p>
        </div>
        <div className="bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] p-4 text-left space-y-2 text-sm">
          <div className="flex items-center gap-2 text-[#1A2E6E]">
            <Calendar size={14} className="text-[#1A2E6E]/50" />
            {formatDate(selectedDate)}
          </div>
          <div className="flex items-center gap-2 text-[#1A2E6E]">
            <Clock size={14} className="text-[#1A2E6E]/50" />
            {selectedSlot.start} – {selectedSlot.end}
          </div>
          <div className="flex items-center gap-2 text-[#1A2E6E]">
            <User size={14} className="text-[#1A2E6E]/50" />
            {bookedClass.teacher_name}
          </div>
        </div>
        {mode === 'page' && (
          <Link
            to="/student/classes"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#D72B2B] text-white rounded-xl font-semibold hover:bg-[#B71C1C] transition-colors"
          >
            View My Classes <ArrowRight size={16} />
          </Link>
        )}
      </motion.div>
    );

    if (mode === 'embed') return content;
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">{content}</div>
    );
  }

  const mainContent = (
    <div className={`${mode === 'page' ? 'max-w-3xl mx-auto space-y-6' : 'space-y-5'}`}>
      {mode === 'page' && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Schedule a 1:1 Class</h1>
            <p className="text-[#1A2E6E]/60 mt-1">Pick an available slot with your teacher.</p>
          </div>
          <Link
            to="/student/available"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E8EAF0] rounded-xl text-sm font-medium text-[#1A2E6E] hover:bg-[#F5F7FA] transition-colors"
          >
            <CalendarDays size={14} />
            View open classes
          </Link>
        </div>
      )}

      {/* Teacher selector */}
      {teachers.length > 1 && (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4">
          <p className="text-sm font-medium text-[#1A2E6E] mb-3">Choose Teacher</p>
          <div className="flex flex-wrap gap-2">
            {teachers.map(t => (
              <button
                key={t.id}
                onClick={() => { setSelectedTeacher(t); setSelectedDate(null); setSelectedSlot(null); }}
                data-testid={`schedule-teacher-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  selectedTeacher?.id === t.id
                    ? 'border-[#1A2E6E] bg-[#1A2E6E]/5 text-[#1A2E6E]'
                    : 'border-[#E8EAF0] text-[#1A2E6E]/60 hover:border-[#1A2E6E]/30'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-[#1A2E6E] flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {t.name.charAt(0)}
                </div>
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calendar + Slots */}
      <div className={`grid gap-5 ${mode === 'page' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Calendar */}
        {selectedTeacher && (
          <MiniCalendar
            teacherId={selectedTeacher.id}
            selectedDate={selectedDate}
            onSelectDate={handleDateSelect}
          />
        )}

        {/* Slots */}
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4">
          {!selectedDate ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CalendarDays className="w-10 h-10 text-[#1A2E6E]/20 mb-3" />
              <p className="text-sm text-[#1A2E6E]/50">Select a date on the calendar<br />to see available slots.</p>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-[#1A2E6E] mb-3">
                {formatDate(selectedDate)}
              </p>
              <SlotPicker
                teacherId={selectedTeacher?.id}
                date={selectedDate}
                onSelect={handleSlotSelect}
                selectedSlot={selectedSlot}
                bookingState={bookingState}
              />
            </>
          )}
        </div>
      </div>

      {/* Confirm booking */}
      <AnimatePresence>
        {selectedSlot && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="bg-white rounded-xl border border-[#E8EAF0] p-5"
          >
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="space-y-1">
                <p className="font-semibold text-[#1A2E6E]">Confirm booking</p>
                <p className="text-sm text-[#1A2E6E]/60 flex items-center gap-1.5">
                  <Calendar size={13} /> {formatDate(selectedDate)}
                </p>
                <p className="text-sm text-[#1A2E6E]/60 flex items-center gap-1.5">
                  <Clock size={13} /> {selectedSlot.start} – {selectedSlot.end} (60 min)
                </p>
                <p className="text-sm text-[#1A2E6E]/60 flex items-center gap-1.5">
                  <User size={13} /> {selectedTeacher?.name}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                {mode === 'embed' && onSkip && (
                  <button
                    onClick={onSkip}
                    data-testid="schedule-skip-btn"
                    className="px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E]/70 rounded-xl text-sm font-medium hover:bg-[#F5F7FA] transition-colors"
                  >
                    Skip for now
                  </button>
                )}
                <button
                  onClick={handleBook}
                  disabled={bookingState === 'loading'}
                  data-testid="schedule-confirm-booking-btn"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl text-sm font-medium hover:bg-[#B71C1C] disabled:opacity-50 transition-colors"
                >
                  {bookingState === 'loading'
                    ? <><Loader2 size={15} className="animate-spin" /> Booking…</>
                    : <><CheckCircle size={15} /> Confirm Booking</>
                  }
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip link for embed mode when nothing selected */}
      {mode === 'embed' && onSkip && !selectedSlot && (
        <div className="text-center">
          <button onClick={onSkip} className="text-sm text-[#1A2E6E]/40 hover:text-[#1A2E6E] underline">
            Skip for now — I'll schedule later
          </button>
        </div>
      )}
    </div>
  );

  if (mode === 'embed') return mainContent;

  return (
    <div className="px-4 py-6">
      {mainContent}
    </div>
  );
};

export default StudentSchedule;
